import React, { createContext, useContext, useState } from 'react';

type CustomTheme = {
  primaryColor: string;
};

const defaultTheme: CustomTheme = {
  primaryColor: '#2563eb', // blue-600
};

type ThemeCustomizationContextType = {
  customTheme: CustomTheme;
  updateTheme: (newTheme: Partial<CustomTheme>) => void;
  resetTheme: () => void;
};

const ThemeCustomizationContext = createContext<ThemeCustomizationContextType | undefined>(undefined);

export function ThemeCustomizationProvider({ children }: { children: React.ReactNode }) {
  const [customTheme, setCustomTheme] = useState<CustomTheme>(() => {
    const saved = localStorage.getItem('custom-admin-theme');
    return saved ? JSON.parse(saved) : defaultTheme;
  });

  const updateTheme = (newTheme: Partial<CustomTheme>) => {
    setCustomTheme(prev => {
      const updated = { ...prev, ...newTheme };
      localStorage.setItem('custom-admin-theme', JSON.stringify(updated));
      return updated;
    });
  };

  const resetTheme = () => {
    setCustomTheme(defaultTheme);
    localStorage.removeItem('custom-admin-theme');
  };

  return (
    <ThemeCustomizationContext.Provider value={{ customTheme, updateTheme, resetTheme }}>
      {children}
      <style>
        {`
          :root {
            --admin-primary: ${customTheme.primaryColor};
          }
        `}
      </style>
    </ThemeCustomizationContext.Provider>
  );
}

export function useCustomTheme() {
  const context = useContext(ThemeCustomizationContext);
  if (context === undefined) {
    throw new Error('useCustomTheme must be used within a ThemeCustomizationProvider');
  }
  return context;
}
